import React, { useEffect, useState } from 'react';
import { financialAPI, usersAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatDate } from '@/utils/jobStatus';
import { toast } from 'sonner';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ExternalLink, Wallet } from 'lucide-react';

const VideomakerFinancePage = () => {
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stripeLoading, setStripeLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [earnRes, txRes] = await Promise.all([
        financialAPI.getVideomakerEarnings(6),
        financialAPI.getMyTransactions(30),
      ]);
      setEarnings(earnRes.data);
      setTransactions(txRes.data?.transactions || txRes.data || []);
    } catch {
      toast.error('Não foi possível carregar o financeiro.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const summary = earnings?.summary || {};
  const chartData = (earnings?.monthly_earnings || []).map((row) => ({
    month: row._id,
    total: row.total_ganhos,
  }));

  const openStripeOnboarding = async () => {
    setStripeLoading(true);
    try {
      const { data } = await usersAPI.createStripeConnect();
      const url = data.onboarding_url || data.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else toast.error('Link de onboarding indisponível.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao abrir Stripe Connect.');
    } finally {
      setStripeLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    setStripeLoading(true);
    try {
      const { data } = await usersAPI.getStripeDashboard();
      const url = data.url || data.dashboard_url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else toast.error('Painel Stripe indisponível.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Configure o Stripe Connect primeiro.');
    } finally {
      setStripeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48 bg-white/10" />
        <Skeleton className="h-40 w-full bg-white/10" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold flex items-center gap-2">
        <Wallet className="h-7 w-7 text-[#0E76FF]" />
        Financeiro
      </h1>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <p className="text-xs text-white/50">Total liberado (6 meses)</p>
          <p className="text-2xl font-bold mt-1">{formatCurrency(summary.total_ganhos)}</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <p className="text-xs text-white/50">A receber</p>
          <p className="text-2xl font-bold mt-1 text-amber-300">{formatCurrency(summary.a_receber)}</p>
          <p className="text-xs text-white/40 mt-1">{summary.jobs_a_receber || 0} job(s)</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <p className="text-xs text-white/50">Próximo repasse</p>
          <p className="text-sm font-medium mt-2">
            {summary.proxima_data_repasse
              ? formatDate(summary.proxima_data_repasse)
              : '—'}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button
          onClick={openStripeOnboarding}
          disabled={stripeLoading}
          variant="outline"
          className="border-white/20"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Configurar repasses
        </Button>
        <Button
          onClick={openStripeDashboard}
          disabled={stripeLoading}
          className="bg-[#0E76FF] hover:bg-[#2563EB]"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Abrir painel Stripe
        </Button>
      </div>

      {chartData.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 h-64">
          <p className="text-sm text-white/60 mb-4">Ganhos por mês</p>
          <ResponsiveContainer width="100%" height="85%">
            <BarChart data={chartData}>
              <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `R$${v}`} />
              <Tooltip
                contentStyle={{ background: '#0d1528', border: '1px solid rgba(255,255,255,0.1)' }}
                formatter={(v) => [formatCurrency(v), 'Ganhos']}
              />
              <Bar dataKey="total" fill="#0E76FF" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <section>
        <h2 className="text-sm font-medium text-white/60 mb-3">Últimas movimentações</h2>
        <div className="rounded-xl border border-white/10 overflow-hidden">
          {transactions.length === 0 ? (
            <p className="p-4 text-sm text-white/50">Nenhuma transação recente.</p>
          ) : (
            <ul className="divide-y divide-white/10">
              {transactions.slice(0, 20).map((tx) => (
                <li key={tx.id || `${tx.created_at}-${tx.amount}`} className="p-4 flex justify-between gap-4 text-sm">
                  <div>
                    <p className="font-medium">{tx.job_titulo || tx.description || 'Movimentação'}</p>
                    <p className="text-xs text-white/50">{formatDate(tx.created_at || tx.date)}</p>
                  </div>
                  <p className="shrink-0 font-medium">{formatCurrency(tx.valor ?? tx.amount)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
                  </div>
  );
};

export default VideomakerFinancePage;
